import type {
  ApprovalStatus, BillingCycle, InvoiceStatus, InvoiceType,
  PaymentMethod, SubscriptionStatus, TaxTreatment,
} from './types';

export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  STANDARD: 'Invoice',
  PROFORMA: 'Proforma',
  SUBSCRIPTION: 'Subscription',
  MILESTONE: 'Milestone',
  TIME_AND_MATERIAL: 'Time & materials',
  AMC: 'AMC',
  DEBIT_NOTE: 'Debit note',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  PARTIALLY_PAID: 'Part paid',
  PAID: 'Paid',
  OVERDUE: 'Overdue',
  VOID: 'Void',
  WRITTEN_OFF: 'Written off',
};

type Variant = 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline';

export const INVOICE_STATUS_VARIANT: Record<InvoiceStatus, Variant> = {
  DRAFT: 'secondary',
  PENDING_APPROVAL: 'warning',
  APPROVED: 'default',
  SENT: 'default',
  VIEWED: 'default',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'destructive',
  VOID: 'secondary',
  WRITTEN_OFF: 'secondary',
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  BANK_TRANSFER: 'Bank transfer',
  CHEQUE: 'Cheque',
  CARD: 'Card',
  CASH: 'Cash',
  ONLINE_GATEWAY: 'Online',
  ADJUSTMENT: 'Adjustment',
};

export const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const APPROVAL_VARIANT: Record<ApprovalStatus, Variant> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'destructive',
};

export const TAX_TREATMENT_LABELS: Record<TaxTreatment, string> = {
  STANDARD: 'Standard rated',
  ZERO_RATED: 'Zero rated',
  EXEMPT: 'Exempt',
  OUT_OF_SCOPE: 'Out of scope',
  REVERSE_CHARGE: 'Reverse charge',
};

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  HALF_YEARLY: 'Half-yearly',
  ANNUAL: 'Annual',
  ONE_TIME: 'One-time',
};

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  TRIALING: 'Trial',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
  EXPIRED: 'Expired',
};

export const SUBSCRIPTION_STATUS_VARIANT: Record<SubscriptionStatus, Variant> = {
  TRIALING: 'default',
  ACTIVE: 'success',
  PAST_DUE: 'destructive',
  PAUSED: 'warning',
  CANCELLED: 'secondary',
  EXPIRED: 'secondary',
};

export const AGEING_LABELS: Record<string, string> = {
  current: 'Not yet due',
  '1-30': '1–30 days',
  '31-60': '31–60 days',
  '61-90': '61–90 days',
  '90+': 'Over 90 days',
};

/** Adds two decimal-string amounts without going through a float. */
export function addAmounts(a: string | null, b: string | null): string {
  const toMinor = (value: string | null) => {
    if (!value) return 0n;
    const negative = value.startsWith('-');
    const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
    const minor = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
    return negative ? -minor : minor;
  };
  const total = toMinor(a) + toMinor(b);
  const negative = total < 0n;
  const abs = negative ? -total : total;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}
